/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * N-9 / PH-2 (FCR S637) -- one answer to "what do we call a yearly licence?".
 *
 * Two functions used to decide this: `getProductDisplayName` in lib/utils.ts,
 * which composes a display name, and `formatDisplayInterval` in
 * utils/dashboard-formatters.ts, which formats an interval. They asked the same
 * question and agreed only by coincidence of wording. Both had to be edited to
 * translate the suffix, and they are the same shape of duplication that produced
 * SU-10 -- one answer computed in two places, corrected in one.
 *
 * The decision lives here and both call it, so the next change cannot land in
 * one and miss the other.
 */

import i18n from "@/i18n";

/**
 * The licence-type suffix, in the customer's language.
 *
 * These MUST be resolved inside a function. A constant at module scope is
 * evaluated before the customer's language file has loaded and freezes in
 * English -- for exactly the customers the translation is for. That mistake has
 * been found eight times in this codebase and has its own guard.
 */
export const annualLicenseLabel = (): string => i18n.t("common.annualLicense");
export const monthlyLicenseLabel = (): string => i18n.t("dashboard.widgets.monthly");

/** The English forms, which every name written before this change carries. */
const ENGLISH_SUFFIXES = ["Annual License", "Monthly"] as const;

/**
 * Every form a licence-type suffix can take on a name this shop has ever
 * written: the six translations of each, plus the English originals.
 *
 * PH-2 item 11 (S637): this is what the double-append test must compare
 * against.
 *
 * `getProductDisplayName` protected itself against appending twice with a plain
 * substring test -- does the base already contain the suffix? The moment the
 * suffix became a translated string, that test asked whether a FRENCH name
 * contains a FRENCH suffix, while the base name it was testing carried the
 * ENGLISH one, because Stripe's own product names and older invoice
 * descriptions end in "- Annual License". The test answered no, and the
 * customer read "Dictation Pro - Annual License - Licence annuelle". That is not
 * hypothetical: it is the first invoice a French customer opens after the
 * change ships.
 */
const suffixForms = (): string[] => {
  const forms = new Set<string>(ENGLISH_SUFFIXES);
  forms.add(annualLicenseLabel());
  forms.add(monthlyLicenseLabel());
  return [...forms].filter((form) => form.trim().length > 0);
};

/** Escape a string for safe use inside a regular expression. */
const escapeForRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Remove a trailing licence-type suffix in any known form, English included.
 *
 * Returns the name with the suffix and its separator removed. A name that
 * carries no known suffix is returned unchanged.
 */
export const stripLicenseTypeSuffix = (name: string): string => {
  let result = (name ?? "").trim();
  // A name can carry more than one, because an older build may already have
  // doubled it. Strip until nothing more comes off.
  for (;;) {
    const before = result;
    for (const form of suffixForms()) {
      const pattern = new RegExp(`\\s*[-–—]?\\s*${escapeForRegExp(form)}\\s*$`, "i");
      result = result.replace(pattern, "").trim();
    }
    if (result === before) return result;
  }
};

/**
 * Append a licence-type suffix to a product name exactly once, whatever
 * language the name already carries its suffix in.
 */
export const withLicenseTypeSuffix = (name: string, suffix: string): string => {
  const base = stripLicenseTypeSuffix(name);
  if (!suffix.trim()) return base;
  if (!base) return suffix;
  return `${base} - ${suffix}`;
};
